/**
 * Dev21 — design tokens.
 *
 * Every colour / radius / easing the template uses is declared once here and
 * surfaced to the DOM as CSS custom properties (see `themeCssVars`). Sections
 * read them via `var(--d21-*)` so nothing is hard-coded at the section level
 * and the whole palette can be re-skinned from this single object.
 */

import type { CSSProperties } from 'react';

export interface Dev21Theme {
  /** Primary metallic accent — used for highlights, lines, CTAs. */
  accent: string;
  /** Soft tint of the accent — used for fills and glass tints. */
  accentSoft: string;
  /** Deep text / dark backdrop colour. */
  ink: string;
  /** Warm page background. */
  cream: string;
  /** Card / panel surface. */
  surface: string;
  /** Muted secondary text. */
  muted: string;
  /** Hairline border colour. */
  border: string;
}

export const dev21Theme: Dev21Theme = {
  accent: '#bd8b4e',
  accentSoft: '#ecdcc1',
  ink: '#211a13',
  cream: '#faf6ef',
  surface: '#ffffff',
  muted: '#8c7f6d',
  border: 'rgba(33,26,19,0.10)',
};

/** Shared cubic-bezier easing for every Dev21 transition. */
export const DEV21_EASE = [0.22, 1, 0.36, 1] as const;

/** Map the theme object onto the CSS variables consumed by the sections. */
export function themeCssVars(theme: Dev21Theme): CSSProperties {
  return {
    '--d21-accent': theme.accent,
    '--d21-accent-soft': theme.accentSoft,
    '--d21-ink': theme.ink,
    '--d21-cream': theme.cream,
    '--d21-surface': theme.surface,
    '--d21-muted': theme.muted,
    '--d21-border': theme.border,
  } as CSSProperties;
}
